import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal variant types for different use cases
 */
export type ModalVariant = 'confirm' | 'prompt' | 'alert';

/**
 * Props for the Modal component
 */
interface ModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback when modal is closed (cancel/backdrop click/escape) */
  onClose: () => void;
  /** Callback when action is confirmed */
  onConfirm: (value?: string) => void;
  /** Modal title */
  title: string;
  /** Modal message/description */
  message?: string;
  /** Modal variant: 'confirm' for yes/no, 'prompt' for text input, 'alert' for ok only */
  variant?: ModalVariant;
  /** Text for confirm button */
  confirmText?: string;
  /** Text for cancel button */
  cancelText?: string;
  /** Placeholder for prompt input */
  placeholder?: string;
  /** Initial value for prompt input */
  initialValue?: string;
  /** Whether confirm action is destructive (shows red button) */
  isDestructive?: boolean;
}

/**
 * Modal - Reusable modal dialog component
 *
 * Replaces native browser dialogs (alert, confirm, prompt) with styled UI:
 *
 * - `alert` variant: Single OK button for acknowledgment
 * - `confirm` variant: Yes/No buttons for confirmation
 * - `prompt` variant: Text input with OK/Cancel buttons
 *
 * @example
 * ```tsx
 * // Confirmation dialog
 * <Modal
 *   isOpen={showConfirm}
 *   onClose={() => setShowConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Task"
 *   message="Are you sure you want to delete this task?"
 *   variant="confirm"
 *   isDestructive
 * />
 *
 * // Prompt dialog
 * <Modal
 *   isOpen={showPrompt}
 *   onClose={() => setShowPrompt(false)}
 *   onConfirm={(name) => createTask(name)}
 *   title="New Task"
 *   message="Enter the task name:"
 *   variant="prompt"
 *   placeholder="Task name..."
 * />
 * ```
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  variant = 'confirm',
  confirmText,
  cancelText = 'Cancel',
  placeholder = '',
  initialValue = '',
  isDestructive = false,
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset input value when modal opens
  useEffect(() => {
    if (isOpen) {
      setInputValue(initialValue);
    }
  }, [isOpen, initialValue]);

  // Focus input when modal opens (for prompt variant)
  useEffect(() => {
    if (isOpen && variant === 'prompt' && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen, variant]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && variant !== 'prompt') {
        onConfirm();
      } else if (e.key === 'Enter' && variant === 'prompt' && inputValue.trim()) {
        onConfirm(inputValue.trim());
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onConfirm, variant, inputValue]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (variant === 'prompt') {
      if (inputValue.trim()) {
        onConfirm(inputValue.trim());
      }
    } else {
      onConfirm();
    }
  }, [variant, inputValue, onConfirm]);

  const getConfirmText = () => {
    if (confirmText) return confirmText;
    switch (variant) {
      case 'alert': return 'OK';
      case 'confirm': return isDestructive ? 'Delete' : 'Confirm';
      case 'prompt': return 'OK';
      default: return 'OK';
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-header">
          <h3 id="modal-title" className="modal-title">{title}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        {message && (
          <div className="modal-body">
            <p className="modal-message">{message}</p>
          </div>
        )}

        {variant === 'prompt' && (
          <div className="modal-input-container">
            <input
              ref={inputRef}
              type="text"
              className="modal-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={placeholder}
              aria-label={placeholder || 'Input'}
            />
          </div>
        )}

        <div className="modal-footer">
          {variant !== 'alert' && (
            <button className="modal-button modal-button-cancel" onClick={onClose}>
              {cancelText}
            </button>
          )}
          <button
            className={`modal-button modal-button-confirm ${isDestructive ? 'modal-button-destructive' : ''}`}
            onClick={handleConfirm}
            disabled={variant === 'prompt' && !inputValue.trim()}
          >
            {getConfirmText()}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
